import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useRouter } from 'vue-router'

import type { Induction, User } from '@/types/models'

import apolloClient from '@/apollo'
import { CurrentUser as CurrentUserQuery } from '@/graphql/user/user.queries.ts'
import i18n from '@/i18n'
import { useAdminStore } from '@/store/admin.ts'
import { useEventStore } from '@/store/event.ts'
import { useInductionStore } from '@/store/induction.ts'
import { useManagerMenuStore } from '@/store/managerMenu.ts'
import { useNoticeStore } from '@/store/notice.ts'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()

  const initiated = ref(false)

  const user = ref<Partial<User>>({
    id: '0',
  })

  const addInduction = async (induction: Induction) => {
    const loggedIn = await ensureIsLoggedIn()
    if (!loggedIn) return

    user.value = {
      ...user.value,
      inductions: [...(user.value.inductions ?? []), induction],
    }
  }

  const ensureIsLoggedIn = async () => {
    if (user.value.id === '0') {
      await router.push({ name: 'signin' })
      return false
    }

    return true
  }

  const goToRoleDashboard = async () => {
    if (user.value.is_admin === true && router.currentRoute.value.name !== 'admin') {
      await router.push({ name: 'admin' })
    } else if (
      user.value.is_site_manager === true &&
      router.currentRoute.value.name !== 'manager'
    ) {
      await router.push({ name: 'manager' })
    } else if (
      user.value.is_contractor === true &&
      router.currentRoute.value.name !== 'contractor'
    ) {
      await router.push({ name: 'contractor' })
    } else {
      await router.push({ name: 'signin' })
    }
  }

  const guardAdminRoutes = async () => {
    if (!(await ensureIsLoggedIn())) return

    if (user.value.is_admin !== true) {
      return await goToRoleDashboard()
    }
  }

  const guardContractorRoutes = async () => {
    if (!(await ensureIsLoggedIn())) return

    if (user.value.is_contractor !== true) {
      return await goToRoleDashboard()
    }
  }

  const guardManagerRoutes = async () => {
    if (!(await ensureIsLoggedIn())) return

    if (user.value.is_site_manager !== true) {
      return await goToRoleDashboard()
    }
  }

  const setUser = async (
    payload: { access_token?: string; refresh_token?: string; user: Partial<User> },
    redirectToRoleDashboad = false,
  ) => {
    if (payload.access_token) {
      localStorage.setItem('access_token', payload.access_token)
    }
    if (payload.refresh_token) {
      localStorage.setItem('refresh_token', payload.refresh_token)
    }
    user.value = structuredClone(payload.user)

    if (user.value.preferred_lang) {
      i18n.global.locale.value = user.value.preferred_lang
    }

    if (redirectToRoleDashboad) {
      await goToRoleDashboard()
    }
  }

  const clearState = () => {
    user.value = { id: '0' }
    initiated.value = false
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    i18n.global.locale.value = navigator.language.split('-').at(0) ?? 'en'
    useAdminStore().$reset()
    useInductionStore().$reset()
    useManagerMenuStore().$reset()
    useEventStore().$reset()
    useNoticeStore().$reset()
    apolloClient.clearStore().then()
  }

  const logout = () => {
    clearState()
    useNoticeStore().set({
      message: i18n.global.t('logout_success'),
      ttl: 5_000,
      type: 'success',
    })
  }

  const verifyCurrentSession = async () => {
    if (user.value.id !== '0' || initiated.value) return

    if (!localStorage.getItem('access_token')) {
      initiated.value = true
      return
    }

    try {
      const { data } = await apolloClient.query({ query: CurrentUserQuery })

      await setUser({ user: data.me }, false)
    } catch {
      clearState()
      useNoticeStore().set({
        message: i18n.global.t('session_expired'),
        ttl: 5_000,
        type: 'warning',
      })
      if (router.currentRoute.value.name !== 'signin') {
        router.push({ name: 'signin' })
        initiated.value = true
      }
    }
  }

  return {
    user,
    addInduction,
    guardAdminRoutes,
    guardContractorRoutes,
    guardManagerRoutes,
    logout,
    setUser,
    verifyCurrentSession,
  }
})
